/**
 * DepartmentDetailPage Component
 * Displays detailed information about a department including managers and teams
 */

import { useState, useEffect, useMemo } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DepartmentFormModal } from '../components/departments/DepartmentFormModal';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Breadcrumbs, getRootBreadcrumb, type BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { departmentsService } from '../services/departments.service';
import { teamsService, type Team } from '../services/teams.service';
import { membersService } from '../services/members.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getInitials } from '../lib/utils';

export const DepartmentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false);
  const [selectedTeamToAdd, setSelectedTeamToAdd] = useState<Team | null>(null);
  const [deptLogoUrl, setDeptLogoUrl] = useState<string | null>(null);
  const [managerImageUrls, setManagerImageUrls] = useState<Map<number, string>>(new Map());
  const [directorImageUrls, setDirectorImageUrls] = useState<Map<number, string>>(new Map());
  const [teamLogoUrls, setTeamLogoUrls] = useState<Map<number, string>>(new Map());

  const deptId = Number(id);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Fetch department details (includes managers)
  const { data: department, isLoading: isLoadingDept, dataUpdatedAt } = useQuery({
    queryKey: ['department', deptId],
    queryFn: () => departmentsService.getById(deptId),
    enabled: !!deptId,
  });

  // Fetch teams in this department
  const { data: teamsResponse, isLoading: isLoadingTeams } = useQuery({
    queryKey: ['teams', { department_id: deptId }],
    queryFn: () => teamsService.getPaginated({ department_id: deptId, limit: 100 }),
    enabled: !!deptId,
  });

  const teams = teamsResponse?.data ?? [];

  // Build breadcrumb hierarchy
  const breadcrumbItems = useMemo(() => {
    if (!department) return [];
    const items: BreadcrumbItem[] = [getRootBreadcrumb()];
    items.push({ label: 'Departments', to: '/departments' });
    return items;
  }, [department]);

  // Fetch all members (for add manager picker)
  const { data: allMembers = [] } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
  });

  // Fetch all teams (for add team picker)
  const { data: allTeams = [] } = useQuery({
    queryKey: ['teams-all'],
    queryFn: teamsService.getAll,
  });

  // Load department logo
  useEffect(() => {
    if (!department?.has_department_logo) {
      setDeptLogoUrl(null);
      return;
    }

    let cancelled = false;
    setDeptLogoUrl(null);

    departmentsService.fetchDepartmentImage(department.id).then((url) => {
      if (!cancelled && url) {
        setDeptLogoUrl(url);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [department?.id, department?.has_department_logo, dataUpdatedAt]);

  // Load manager profile images
  useEffect(() => {
    if (!department?.managers) return;
    let cancelled = false;
    const newUrls = new Map<number, string>();

    const loadImages = async () => {
      for (const mgr of department.managers) {
        if (mgr.has_profile_picture) {
          try {
            const url = await membersService.fetchMemberImage(mgr.member_id);
            if (url && !cancelled) {
              newUrls.set(mgr.member_id, url);
            }
          } catch (error) {
            console.error(`Failed to load image for member ${mgr.member_id}`, error);
          }
        }
      }
      if (!cancelled) {
        setManagerImageUrls(newUrls);
      }
    };

    loadImages();
    return () => { cancelled = true; };
  }, [department?.managers]);

  // Load director profile images (parallel to managers)
  useEffect(() => {
    if (!department?.directors) return;
    let cancelled = false;
    const newUrls = new Map<number, string>();

    const loadImages = async () => {
      for (const dir of department.directors) {
        if (dir.has_profile_picture) {
          try {
            const url = await membersService.fetchMemberImage(dir.member_id);
            if (url && !cancelled) {
              newUrls.set(dir.member_id, url);
            }
          } catch (error) {
            console.error(`Failed to load image for member ${dir.member_id}`, error);
          }
        }
      }
      if (!cancelled) {
        setDirectorImageUrls(newUrls);
      }
    };

    loadImages();
    return () => { cancelled = true; };
  }, [department?.directors]);

  // Load team logos
  useEffect(() => {
    if (!teams.length) return;
    let cancelled = false;
    const newUrls = new Map<number, string>();

    const loadImages = async () => {
      for (const team of teams) {
        if (team.has_team_logo) {
          try {
            const url = await teamsService.fetchTeamImage(team.id);
            if (url && !cancelled) {
              newUrls.set(team.id, url);
            }
          } catch (error) {
            console.error(`Failed to load logo for team ${team.id}`, error);
          }
        }
      }
      if (!cancelled) {
        setTeamLogoUrls(newUrls);
      }
    };

    loadImages();
    return () => { cancelled = true; };
  }, [teams]);

  // Delete department mutation
  const deleteMutation = useMutation({
    mutationFn: (departmentId: number) => departmentsService.delete(departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department deleted successfully');
      navigate('/departments');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete department. It must be empty (no teams or members).');
    },
  });

  // Add manager mutation
  const addManagerMutation = useMutation({
    mutationFn: (memberId: number) => departmentsService.addManager(deptId, memberId),
    onSuccess: () => {
      toast.success('Manager added successfully');
      queryClient.invalidateQueries({ queryKey: ['department', deptId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add manager');
    },
  });

  // Remove manager mutation
  const removeManagerMutation = useMutation({
    mutationFn: (memberId: number) => departmentsService.removeManager(deptId, memberId),
    onSuccess: () => {
      toast.success('Manager removed');
      queryClient.invalidateQueries({ queryKey: ['department', deptId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove manager');
    },
  });

  // Add director mutation
  const addDirectorMutation = useMutation({
    mutationFn: (memberId: number) => departmentsService.addDirector(deptId, memberId),
    onSuccess: () => {
      toast.success('Director added successfully');
      queryClient.invalidateQueries({ queryKey: ['department', deptId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add director');
    },
  });

  // Remove director mutation
  const removeDirectorMutation = useMutation({
    mutationFn: (memberId: number) => departmentsService.removeDirector(deptId, memberId),
    onSuccess: () => {
      toast.success('Director removed');
      queryClient.invalidateQueries({ queryKey: ['department', deptId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove director');
    },
  });

  // Role-swap mutation: removes a member from one role then adds to the other.
  // Triggered when admin picks a member already holding the cross role.
  const swapMutation = useMutation({
    mutationFn: async (params: { memberId: number; from: 'director' | 'manager'; to: 'director' | 'manager' }) => {
      if (params.from === 'director') {
        await departmentsService.removeDirector(deptId, params.memberId);
      } else {
        await departmentsService.removeManager(deptId, params.memberId);
      }
      if (params.to === 'director') {
        await departmentsService.addDirector(deptId, params.memberId);
      } else {
        await departmentsService.addManager(deptId, params.memberId);
      }
    },
    onSuccess: () => {
      toast.success('Role updated');
      queryClient.invalidateQueries({ queryKey: ['department', deptId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to change role');
    },
  });

  // Assign team to department mutation
  const assignTeamMutation = useMutation({
    mutationFn: (team: Team) =>
      teamsService.update(team.id, { name: team.name, department_id: deptId }),
    onSuccess: () => {
      toast.success('Team added to department');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['department', deptId] });
      setIsAddTeamModalOpen(false);
      setSelectedTeamToAdd(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add team to department');
    },
  });

  const handleDeleteDepartment = () => {
    if (department) {
      deleteMutation.mutate(department.id);
    }
  };

  // When admin picks a member already holding the cross role, we don't silently swap —
  // we surface a confirmation modal first so it's an explicit decision.
  const [pendingSwap, setPendingSwap] = useState<{
    memberId: number;
    memberName: string;
    from: 'director' | 'manager';
    to: 'director' | 'manager';
  } | null>(null);

  const currentManagerIds = department?.managers?.map((m) => m.member_id) || [];
  const currentDirectorIds = department?.directors?.map((d) => d.member_id) || [];

  const handleAddManager = (memberIdStr: string) => {
    if (!memberIdStr) return;
    const memberId = Number(memberIdStr);
    if (currentDirectorIds.includes(memberId)) {
      const m = allMembers.find((x) => x.id === memberId);
      if (m) {
        setPendingSwap({
          memberId,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'director',
          to: 'manager',
        });
      }
      return;
    }
    addManagerMutation.mutate(memberId);
  };

  const handleAddDirector = (memberIdStr: string) => {
    if (!memberIdStr) return;
    const memberId = Number(memberIdStr);
    if (currentManagerIds.includes(memberId)) {
      const m = allMembers.find((x) => x.id === memberId);
      if (m) {
        setPendingSwap({
          memberId,
          memberName: `${m.first_name} ${m.last_name}`,
          from: 'manager',
          to: 'director',
        });
      }
      return;
    }
    addDirectorMutation.mutate(memberId);
  };

  // Picker candidates exclude only the same-role members; cross-role members stay
  // selectable so admin can opt into the swap flow via the confirmation modal.
  const availableManagerMembers = allMembers.filter(
    (m) => !m.removed_at && !currentManagerIds.includes(m.id)
  );
  const availableDirectorMembers = allMembers.filter(
    (m) => !m.removed_at && !currentDirectorIds.includes(m.id)
  );

  // Filter available teams for add team picker (exclude teams already in this department)
  const availableTeams = allTeams.filter((t) => t.department_id !== deptId);

  // Handle team selection from dropdown
  const handleTeamSelect = (teamId: string) => {
    if (!teamId) {
      setSelectedTeamToAdd(null);
      return;
    }
    const team = allTeams.find((t) => t.id === Number(teamId));
    if (team) {
      setSelectedTeamToAdd(team);
    }
  };

  // Handle add team confirm
  const handleConfirmAddTeam = () => {
    if (selectedTeamToAdd) {
      assignTeamMutation.mutate(selectedTeamToAdd);
    }
  };

  // Format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };

  if (isLoadingDept || isLoadingTeams) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!department) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-heading mb-2">Department Not Found</h2>
          <p className="text-meta mb-4">The department you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/departments')}>Back to Departments</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center justify-between">
          <Breadcrumbs items={breadcrumbItems} />
          <Button variant="secondary" className="hidden lg:inline-flex" onClick={() => navigate('/departments')}>
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Departments
          </Button>
        </div>

        {/* Department Header */}
        <div className="bg-surface rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            {/* Left: Department Info */}
            <div className="flex items-center space-x-4">
              {/* Department Logo */}
              <div className="flex-shrink-0">
                {deptLogoUrl ? (
                  <img
                    src={deptLogoUrl}
                    alt={department.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-2xl font-bold">
                    {getInitials(department.name.split(' ')[0], department.name.split(' ')[1] || '')}
                  </div>
                )}
              </div>

              {/* Department Details */}
              <div>
                <h1 className="text-2xl font-bold text-heading">{department.name}</h1>
                {department.description && (
                  <p className="mt-1 text-sm text-meta max-w-lg">{department.description}</p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-meta">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {department.team_count} team{department.team_count !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {department.member_count} member{department.member_count !== 1 ? 's' : ''}
                  </span>
                  <span className="hidden lg:flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    Created {formatDate(department.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Action Buttons (Admin Only) — visible at md+; mobile copy lives below the card */}
            {hasAdminAccess(user?.role) && (
              <div className="hidden md:flex items-center space-x-9">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content: Teams + Managers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Teams Section */}
          <div className="lg:col-span-2">
            <div className="bg-surface rounded-lg shadow">
              <div className="p-6 border-b border-[var(--border-default)] flex items-center justify-between">
                <h2 className="text-xl font-semibold text-heading flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Teams ({teams.length})
                </h2>
                {hasAdminAccess(user?.role) && (
                  <Button size="sm" onClick={() => setIsAddTeamModalOpen(true)}>
                    <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Team
                  </Button>
                )}
              </div>

              {teams.length === 0 ? (
                <div className="p-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-meta mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-sm text-meta">No teams in this department yet</p>
                  {hasAdminAccess(user?.role) && (
                    <Button onClick={() => setIsAddTeamModalOpen(true)} className="mt-3">
                      <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add First Team
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="group relative p-4 flex items-center space-x-4 hover:bg-surface-hover active:bg-surface-hover transition-colors"
                    >
                      {/* Full-row tap target on touch devices */}
                      <Link
                        to={`/teams/${team.id}`}
                        state={{ backLabel: 'Department' }}
                        className="absolute inset-0 z-10 can-hover:hidden"
                        tabIndex={-1}
                        aria-label={`View ${team.name}`}
                      />

                      {/* Team Logo */}
                      <Link
                        to={`/teams/${team.id}`}
                        state={{ backLabel: 'Department' }}
                        className="flex-shrink-0 relative z-20"
                      >
                        {teamLogoUrls.get(team.id) ? (
                          <img
                            src={teamLogoUrls.get(team.id)}
                            alt={team.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-bold">
                            {getInitials(team.name, '')}
                          </div>
                        )}
                      </Link>

                      {/* Team Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-heading truncate">
                          <Link
                            to={`/teams/${team.id}`}
                            state={{ backLabel: 'Department' }}
                            className="relative z-20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          >
                            {team.name}
                          </Link>
                        </h3>
                        <p className="text-sm text-meta">
                          {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                        </p>
                      </div>

                      {/* Tap affordance — touch devices only */}
                      <div className="hidden touch:flex items-center text-meta flex-shrink-0" aria-hidden="true">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Directors + Managers Sections */}
          <div className="lg:col-span-1 space-y-6">
            {/* Directors Section */}
            <div className="bg-surface rounded-lg shadow">
              <div className="p-6 border-b border-[var(--border-default)]">
                <h2 className="text-xl font-semibold text-heading flex items-center">
                  <svg className="w-5 h-5 mr-2 text-status-accent" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Directors ({department.directors?.length || 0})
                </h2>
              </div>

              {department.directors && department.directors.length > 0 ? (
                <div className="divide-y divide-[var(--border-default)]">
                  {department.directors.map((dir) => (
                    <div key={dir.member_id} className="p-4 flex items-center justify-between hover:bg-surface-hover transition-colors">
                      <div className="flex items-center space-x-3 min-w-0">
                        <Link
                          to={`/members/${dir.member_id}`}
                          state={{ backLabel: 'Department' }}
                          className="flex-shrink-0"
                        >
                          {directorImageUrls.get(dir.member_id) ? (
                            <img
                              src={directorImageUrls.get(dir.member_id)}
                              alt={`${dir.first_name} ${dir.last_name}`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-medium">
                              {getInitials(dir.first_name, dir.last_name)}
                            </div>
                          )}
                        </Link>
                        <div className="min-w-0">
                          <Link
                            to={`/members/${dir.member_id}`}
                            state={{ backLabel: 'Department' }}
                            className="text-sm font-medium text-heading hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate block"
                          >
                            {dir.first_name} {dir.last_name}
                          </Link>
                          <p className="text-xs text-meta truncate">{dir.email}</p>
                        </div>
                      </div>
                      {hasAdminAccess(user?.role) && (
                        <button
                          onClick={() => removeDirectorMutation.mutate(dir.member_id)}
                          className="flex-shrink-0 ml-2 p-1 text-meta hover:text-red-500 transition-colors"
                          title="Remove director"
                          disabled={removeDirectorMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-sm text-meta">No directors assigned</p>
                </div>
              )}

              {hasAdminAccess(user?.role) && (
                <div className="p-4 border-t border-[var(--border-default)]">
                  <SearchableSelect
                    value=""
                    onChange={handleAddDirector}
                    options={[
                      { value: '', label: 'Add a director...' },
                      ...availableDirectorMembers.map((m) => ({
                        value: String(m.id),
                        label: `${m.first_name} ${m.last_name}`,
                      })),
                    ]}
                    placeholder="Search members..."
                  />
                </div>
              )}
            </div>

            {/* Managers Section */}
            <div className="bg-surface rounded-lg shadow">
              <div className="p-6 border-b border-[var(--border-default)]">
                <h2 className="text-xl font-semibold text-heading flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Managers ({department.managers?.length || 0})
                </h2>
              </div>

              {/* Manager List */}
              {department.managers && department.managers.length > 0 ? (
                <div className="divide-y divide-[var(--border-default)]">
                  {department.managers.map((mgr) => (
                    <div key={mgr.member_id} className="p-4 flex items-center justify-between hover:bg-surface-hover transition-colors">
                      <div className="flex items-center space-x-3 min-w-0">
                        <Link
                          to={`/members/${mgr.member_id}`}
                          state={{ backLabel: 'Department' }}
                          className="flex-shrink-0"
                        >
                          {managerImageUrls.get(mgr.member_id) ? (
                            <img
                              src={managerImageUrls.get(mgr.member_id)}
                              alt={`${mgr.first_name} ${mgr.last_name}`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-medium">
                              {getInitials(mgr.first_name, mgr.last_name)}
                            </div>
                          )}
                        </Link>
                        <div className="min-w-0">
                          <Link
                            to={`/members/${mgr.member_id}`}
                            state={{ backLabel: 'Department' }}
                            className="text-sm font-medium text-heading hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate block"
                          >
                            {mgr.first_name} {mgr.last_name}
                          </Link>
                          <p className="text-xs text-meta truncate">{mgr.email}</p>
                        </div>
                      </div>
                      {hasAdminAccess(user?.role) && (
                        <button
                          onClick={() => removeManagerMutation.mutate(mgr.member_id)}
                          className="flex-shrink-0 ml-2 p-1 text-meta hover:text-red-500 transition-colors"
                          title="Remove manager"
                          disabled={removeManagerMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <svg className="mx-auto h-10 w-10 text-meta mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p className="text-sm text-meta">No managers assigned</p>
                </div>
              )}

              {/* Add Manager (Admin Only) */}
              {hasAdminAccess(user?.role) && (
                <div className="p-4 border-t border-[var(--border-default)]">
                  <SearchableSelect
                    value=""
                    onChange={handleAddManager}
                    options={[
                      { value: '', label: 'Add a manager...' },
                      ...availableManagerMembers.map((m) => ({
                        value: String(m.id),
                        label: `${m.first_name} ${m.last_name}`,
                      })),
                    ]}
                    placeholder="Search members..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile-only Action Buttons (below 768px) — at the bottom of the page on the darker background */}
        {hasAdminAccess(user?.role) && (
          <div className="md:hidden flex items-center space-x-9">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsEditModalOpen(true)}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Edit Department Modal */}
      <DepartmentFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        departmentId={department.id}
      />

      {/* Role-swap Confirmation Modal */}
      {pendingSwap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Change role</h3>
            <p className="text-sm text-meta mb-6">
              <strong>{pendingSwap.memberName}</strong> is currently a <strong>{pendingSwap.from}</strong>{' '}
              of this department. Assigning them as <strong>{pendingSwap.to}</strong> will remove the{' '}
              {pendingSwap.from} role.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setPendingSwap(null)}
                disabled={swapMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const params = pendingSwap;
                  setPendingSwap(null);
                  swapMutation.mutate(params);
                }}
                isLoading={swapMutation.isPending}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Delete Department</h3>
            <p className="text-sm text-meta mb-6">
              Are you sure you want to delete "{department.name}"? The department must be empty (no teams or members).
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteDepartment}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Add Team Modal */}
      <Modal
        isOpen={isAddTeamModalOpen}
        onClose={() => { setIsAddTeamModalOpen(false); setSelectedTeamToAdd(null); }}
        title="Add Team to Department"
      >
        <div className="space-y-4">
          <SearchableSelect
            label="Select a team"
            value={selectedTeamToAdd ? String(selectedTeamToAdd.id) : ''}
            onChange={handleTeamSelect}
            options={[
              { value: '', label: 'Choose a team...' },
              ...availableTeams.map((t) => ({
                value: String(t.id),
                label: t.name + (t.department_name ? ` (${t.department_name})` : ''),
              })),
            ]}
            placeholder="Search teams..."
          />

          {/* Transfer warning */}
          {selectedTeamToAdd && selectedTeamToAdd.department_id && (
            <div className="rounded-md bg-status-warning-subtle border border-status-warning p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Team transfer</h3>
                  <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                    <strong>{selectedTeamToAdd.name}</strong> currently belongs to <strong>{selectedTeamToAdd.department_name}</strong>.
                    Adding it here will transfer the team and its {selectedTeamToAdd.member_count ?? 0} member{selectedTeamToAdd.member_count !== 1 ? 's' : ''} to <strong>{department.name}</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => { setIsAddTeamModalOpen(false); setSelectedTeamToAdd(null); }}
              disabled={assignTeamMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAddTeam}
              disabled={!selectedTeamToAdd}
              isLoading={assignTeamMutation.isPending}
            >
              {selectedTeamToAdd?.department_id ? 'Transfer Team' : 'Add Team'}
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};
